import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore"
import SendIcon from "@mui/icons-material/Send"
import { AlertTitle, Divider, Stack } from "@mui/material"
import { Fragment, ReactElement, useContext, useEffect, useState } from "react"
import { useForm } from "react-hook-form"

import SubmitApplicationSuccess from "./submitApplicationSuccess"
import {
    CONFIRM_TITLE,
    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE,
    UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING,
} from "../../../../constants/generalConstants"
import {
    GENERIC_DISPLAY_DETAIL,
    SUPPORT_REFER_ENTITY,
} from "../../../../errors/errorMessages"
import useSubmitApplication from "../../../../services/middleware/hooks/singleApplication/useSubmitApplication"
import { GRID_SPACING } from "../../../../themes/defaultThemeParams"
import { getValue } from "../../../../utils/general/generalUtils"
import {
    createDevicesNestedObj,
    stripArraysAndObjectValues,
} from "../../../../utils/submitApplication/submitApplicationUtils"
import AppLoadingButton from "../../../buttons/appLoadingButton"
import AppAlert from "../../../dataDisplay/alerts/appAlert"
import { NestedKeyValueListType } from "../../../dataDisplay/nestedVerticalKeyValueList"
import FormHeader from "../../../formHeader/formHeader"
import FormPageSummary from "../../../formPageSummary/formPageSummary"
import ValidatedCheckBox from "../../../inputs/validated/validatedCheckBox"
import ProvideMpanManually from "../../../popUps/provideMpanManually/provideMpanManually"
import SubmitConfirmation from "../../../popUps/submitConfirmation/submitConfirmation"
import { ApplicationContext } from "../applicationProvider"
import {
    ATTACHMENT_FORM_TYPES,
    RECOGNISED_INPUT_ID_KW,
} from "../recognisedForms/recongisedFormKeys"
import { LCT_TILES } from "../selectLct/applicationTypeTiles"

interface MappedValues {
    [key: string]: string
}

/**
 * Displays the application review page. This is the final step in the
 * application process. The user can review the information they have
 * entered and submit the application.
 */
const Review = (): ReactElement => {
    const {
        error,
        isSubmitting,
        isSuccessDialogOpen,
        isMpanNotFoundDialogOpen,
        postApplication,
        dno,
        applicationId,
        isConfirmDialogOpen,
        setIsConfirmDialogOpen,
        duplicateDetails,
        submissionDetail,
    }: {
        error: any
        isSubmitting: boolean
        isSuccessDialogOpen: boolean
        isMpanNotFoundDialogOpen: boolean
        postApplication: (
            rejectDuplicate: boolean,
            manuallyProvidedMpanInput?: string
        ) => void
        dno: string
        applicationId: string
        isConfirmDialogOpen: boolean
        setIsConfirmDialogOpen: (value: boolean) => void
        duplicateDetails: any
        submissionDetail: string
    } = useSubmitApplication()

    const {
        formData,
        setCurrentStepIndex,
        displayStepTitles,
        rawApplicationSpec,
        stepLabels,
        isAmendmentMode,
    } = useContext(ApplicationContext)

    const { control, handleSubmit } = useForm()

    // State for SubmitConfirmationPopup
    const [popupTitle, setPopupTitle] = useState("")
    const [popupMessage, setPopupMessage] = useState("")
    const [popupTableData, setPopupTableData] = useState<
        {
            technologyType: string
            dateOfApplication: string
            applicationId?: string
            isYourApplication: boolean
        }[]
    >([])
    const [showLink, setShowLink] = useState(false)

    const attachmentFormKeys = Object.entries(
        rawApplicationSpec.properties
    ).reduce((acc, [key, formProperty]) => {
        if (
            ATTACHMENT_FORM_TYPES.includes(
                getValue(formProperty, RECOGNISED_INPUT_ID_KW) as string
            )
        ) {
            return [...acc, key]
        }
        return acc
    }, [] as string[])

    const createFieldMapping = (
        prefix: string,
        value: { fileUrl: string }[]
    ): MappedValues => {
        const mappedValues = value.map((v: { fileUrl: string }) => v.fileUrl)
        const result: MappedValues = {}
        mappedValues.forEach((v: string, index: number) => {
            result[`${prefix} ${index + 1}`] = v
        })
        return result
    }

    const submitApplicationManualMpan = (
        manuallyProvidedMpanInput: string
    ): void => {
        postApplication(true, manuallyProvidedMpanInput)
    }

    const openSubmitConfirmationPopup = (): void => {
        let confirmMessage = ""

        // Extract the first non-empty array from duplicates
        const duplicateApplications = [
            ...(duplicateDetails?.yourApplications || []),
            ...(duplicateDetails?.yourCompanyApplications || []),
            ...(duplicateDetails?.otherInstallerApplications || []),
        ]

        const tableData = Array.isArray(duplicateApplications)
            ? duplicateApplications.map((detail) => ({
                  technologyType:
                      detail.lcts
                          ?.map((lct: string) => {
                              const lctTitle = LCT_TILES.find(
                                  (tile) => tile.value === lct
                              )
                              return lctTitle ? lctTitle.title : lct
                          })
                          .join(", ") || "N/A",
                  dateOfApplication: detail?.createdDate
                      ? new Date(detail.createdDate).toLocaleDateString()
                      : "N/A",
                  applicationId:
                      detail?.applicationId ||
                      "N/A - Raised by Installer from other Organisation",
                  isYourApplication: duplicateDetails?.yourApplications?.some(
                      (app: { applicationId: string }) =>
                          app.applicationId === detail.applicationId
                  ),
              }))
            : []

        const mpan = duplicateApplications[0]?.mpan
        confirmMessage =
            `The applications listed below were submitted for the same MPAN: ${mpan} ` +
            `within the past 90 days. Unsubmitted applications can be accessed in the "My Drafts" section. ` +
            `Do you want to Continue?`
        if (duplicateDetails?.yourApplications?.length) {
            setShowLink(true)
        } else {
            setShowLink(false)
        }

        setPopupTitle(CONFIRM_TITLE)
        setPopupMessage(confirmMessage)
        setPopupTableData(tableData)
    }

    // const handleYourApplications

    const handleConfirm = (): void => {
        // On confirm, retry submission with rejectDuplicate = false
        handleSubmit(() => {
            postApplication(false)
        })()

        setIsConfirmDialogOpen(false)
    }

    const handleCancel = (): void => {
        // On cancel, simply close the popup

        setIsConfirmDialogOpen(false)
    }

    // Trigger the confirmation popup when isConfirmDialogOpen is true
    useEffect(() => {
        if (isConfirmDialogOpen) {
            openSubmitConfirmationPopup()
        }
    }, [isConfirmDialogOpen])
    return (
        <>
            {isSuccessDialogOpen && (
                <SubmitApplicationSuccess
                    isOpen={isSuccessDialogOpen}
                    dno={dno}
                    applicationId={applicationId}
                    isAmendmentMode={isAmendmentMode}
                    submissionDetail={submissionDetail}
                />
            )}
            {isMpanNotFoundDialogOpen && (
                <ProvideMpanManually
                    isOpen={isMpanNotFoundDialogOpen}
                    submitApplicationManualMpan={submitApplicationManualMpan}
                />
            )}
            <SubmitConfirmation
                isOpen={isConfirmDialogOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                title={popupTitle}
                message={popupMessage}
                tableData={popupTableData}
                showLink={showLink}
            />
            <Stack direction="column" spacing={GRID_SPACING}>
                <FormHeader
                    header="Review and Submit"
                    subHeader="One final check before submitting!"
                    index={displayStepTitles.length + 1}
                />
                {formData &&
                    Object.entries(formData).map(([formKey, formValues]) => {
                        if (
                            Array.isArray(formValues) &&
                            formValues.length === 0
                        ) {
                            return null
                        }

                        // Do not assume form data is ordered in a way that matches the step labels
                        const idxOfStep = stepLabels.indexOf(formKey)
                        const title = displayStepTitles[idxOfStep]

                        if (attachmentFormKeys.includes(formKey)) {
                            const cutOutImageMapped = createFieldMapping(
                                "Cut-Out Image",
                                formValues as unknown as {
                                    fileUrl: string
                                }[]
                            )

                            return (
                                <Fragment key={formKey}>
                                    <FormPageSummary
                                        key={formKey}
                                        pageTitle={title}
                                        fieldMapping={cutOutImageMapped}
                                        onEdit={() =>
                                            setCurrentStepIndex(idxOfStep)
                                        }
                                    />
                                    <Divider />
                                </Fragment>
                            )
                        }

                        if (Array.isArray(formValues)) {
                            return (
                                <Fragment key={formKey}>
                                    <FormPageSummary
                                        key={formKey}
                                        pageTitle={title}
                                        fieldMapping={
                                            createDevicesNestedObj(
                                                formValues
                                            ) as NestedKeyValueListType
                                        }
                                        onEdit={() =>
                                            setCurrentStepIndex(idxOfStep)
                                        }
                                    />
                                    <Divider />
                                </Fragment>
                            )
                        }

                        return (
                            <Fragment key={formKey}>
                                <FormPageSummary
                                    key={formKey}
                                    pageTitle={title}
                                    onEdit={() =>
                                        setCurrentStepIndex(idxOfStep)
                                    }
                                    fieldMapping={
                                        stripArraysAndObjectValues(
                                            formValues
                                        ) as Record<
                                            string,
                                            | string
                                            | number
                                            | boolean
                                            | undefined
                                        >
                                    }
                                />
                                <Divider />
                            </Fragment>
                        )
                    })}
                {/* We don't want to show the MPAN error here (i.e. which has a 597 code) as we expect the MPAN dialog to open instead */}
                {error &&
                    error.code !== UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE &&
                    error.code !==
                        UNABLE_TO_GET_MPAN_FROM_ECOES_ERROR_CODE_STRING && (
                        <AppAlert
                            severity={error.code === 400 ? "warning" : "error"}
                        >
                            <AlertTitle>
                                {" "}
                                {error.code === 400
                                    ? "We were unable to validate the information you have entered. Please check your inputs and try again"
                                    : error.message}
                            </AlertTitle>
                            {error.code === 400
                                ? `${error.displayDetail}. 
                               If you need help, please contact ${SUPPORT_REFER_ENTITY}`
                                : GENERIC_DISPLAY_DETAIL}
                        </AppAlert>
                    )}
                <Stack
                    direction="row"
                    spacing={GRID_SPACING}
                    alignItems="flex-end"
                    justifyContent="flex-end"
                    paddingTop={3}
                >
                    <ValidatedCheckBox
                        control={control}
                        label="I confirm that the information I have entered is correct and accurate to the best of my knowledge"
                        required
                        registerName="confirmInformation"
                        horizontalMode
                        checkboxOptions={{
                            "": "I confirm",
                        }}
                    />
                </Stack>

                <Stack
                    direction="row"
                    spacing={GRID_SPACING}
                    alignItems="flex-end"
                    justifyContent="flex-end"
                >
                    <AppLoadingButton
                        variant="outlined"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={() => setCurrentStepIndex((prev) => prev - 1)}
                        loading={isSubmitting}
                    >
                        Back
                    </AppLoadingButton>
                    <AppLoadingButton
                        color="success"
                        variant="contained"
                        onClick={handleSubmit(() => {
                            postApplication(true)
                        })}
                        endIcon={<SendIcon />}
                        loading={isSubmitting}
                    >
                        {isAmendmentMode ? "Submit Amendment" : "Submit"}
                    </AppLoadingButton>
                </Stack>
            </Stack>
        </>
    )
}

export default Review
